import { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { CATEGORIES, ICON_OPTIONS, BADGE_COLOR_OPTIONS, STATUS_META } from "../../lib/toolMeta";

const EMPTY = {
  name: "", url: "", category: "Codice & Deploy", icon: "link",
  note: "", status: "online", isPinned: false, badgeColor: "slate",
};

export const ToolFormDialog = ({ open, onOpenChange, onSubmit, initial }) => {
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setForm(initial ? { ...EMPTY, ...initial } : EMPTY);
      setError("");
    }
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = () => {
    if (!form.name.trim()) return setError("Il nome è obbligatorio");
    let url = form.url.trim();
    if (!url) return setError("L'URL è obbligatorio");
    if (!/^https?:\/\//i.test(url)) url = "https://" + url;
    onSubmit({ ...form, name: form.name.trim(), url });
  };

  const isEdit = Boolean(initial);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="tool-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {isEdit ? "Modifica Strumento" : "Aggiungi Strumento"}
          </DialogTitle>
          <DialogDescription>
            {isEdit ? "Aggiorna i dettagli del tuo strumento." : "Aggiungi un nuovo strumento al tuo hub."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid gap-2">
            <Label htmlFor="tool-name">Nome Strumento</Label>
            <Input
              id="tool-name" data-testid="tool-name-input"
              placeholder="es. Supabase Staging"
              value={form.name} onChange={(e) => set("name", e.target.value)}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="tool-url">URL Completo</Label>
            <Input
              id="tool-url" data-testid="tool-url-input"
              placeholder="https://..."
              value={form.url} onChange={(e) => set("url", e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label>Categoria</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="tool-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Icona</Label>
              <Select value={form.icon} onValueChange={(v) => set("icon", v)}>
                <SelectTrigger data-testid="tool-icon-select"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-60">
                  {ICON_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label>Colore Badge</Label>
              <Select value={form.badgeColor} onValueChange={(v) => set("badgeColor", v)}>
                <SelectTrigger data-testid="tool-color-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {BADGE_COLOR_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Stato</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="tool-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="tool-note">Note Personali</Label>
            <Textarea
              id="tool-note" data-testid="tool-note-input"
              placeholder="Note operative, credenziali suggerite, descrizioni..."
              rows={3}
              value={form.note} onChange={(e) => set("note", e.target.value)}
            />
          </div>

          <div className="flex items-center justify-between rounded-xl border border-border bg-secondary/50 px-4 py-3">
            <div>
              <Label htmlFor="tool-pin" className="cursor-pointer">Fissa nei preferiti</Label>
              <p className="text-xs text-muted-foreground">Mostra questo strumento in cima all'hub</p>
            </div>
            <Switch id="tool-pin" data-testid="tool-pin-switch" checked={form.isPinned} onCheckedChange={(v) => set("isPinned", v)} />
          </div>

          {error && <p className="text-sm text-destructive" data-testid="tool-form-error">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="tool-modal-cancel-button">Annulla</Button>
          <Button onClick={handleSave} data-testid="tool-modal-save-button">{isEdit ? "Salva Modifiche" : "Aggiungi Strumento"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
